import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ArrowLeft, ExternalLink, Activity, Shield, Database, Server } from 'lucide-react';
import { getApiBaseUrl, getStatusPageUrl } from '../../../lib/config';
import '../../legal/legal.css';

const SERVICES = [
  { name: 'API', desc: 'rynora-api · authentication, flows, runs', icon: Server },
  { name: 'Runner', desc: 'SaaS / private execution · browser automation', icon: Activity },
  { name: 'Database', desc: 'PostgreSQL · primary store', icon: Database },
  { name: 'Admin console', desc: 'admin.rynora.app', icon: Shield },
];

export function StatusPage() {
  const externalStatus = getStatusPageUrl();
  const [healthOk, setHealthOk] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const apiBase = getApiBaseUrl();
    fetch(`${apiBase}/api/health`)
      .then((r) => {
        if (cancelled) return;
        setHealthOk(r.ok);
      })
      .catch(() => {
        if (!cancelled) setHealthOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dotClass = healthOk === null ? 'is-pending' : healthOk ? 'is-up' : 'is-down';
  const headlineLabel = healthOk === null ? 'Checking…' : healthOk ? 'All systems normal' : 'Some systems degraded';

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <div className="container legal-header-inner">
          <Link to="/" className="nav-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
          <div className="legal-header-actions">
            <Link to="/" className="nav-auth-btn">
              <ArrowLeft size={14} /> Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container legal-body" style={{ gridTemplateColumns: '1fr', maxWidth: 760 }}>
        <article className="legal-article" style={{ maxWidth: 'none' }}>
          <header className="legal-article-head">
            <h1>Rynora status</h1>
            <p className="legal-meta">
              Live health is mirrored to{' '}
              <a href={externalStatus} target="_blank" rel="noreferrer">
                {externalStatus.replace(/^https?:\/\//, '')} <ExternalLink size={12} />
              </a>
            </p>
          </header>

          <div className={`status-headline ${dotClass}`}>
            <span className={`status-dot ${dotClass}`} aria-hidden="true" />
            <strong>{headlineLabel}</strong>
            <a href={externalStatus} target="_blank" rel="noreferrer" className="status-headline-link">
              View detailed status <ExternalLink size={12} />
            </a>
          </div>

          <div className="legal-content">
            <h2>What's covered</h2>
            <ul className="status-services">
              {SERVICES.map(({ name, desc, icon: Icon }) => (
                <li key={name} className="status-service">
                  <span className="status-service-icon">
                    <Icon size={16} />
                  </span>
                  <div>
                    <strong>{name}</strong>
                    <p>{desc}</p>
                  </div>
                  <span className={`status-pill ${dotClass}`}>
                    {healthOk === null ? 'Checking' : healthOk ? 'Operational' : 'Degraded'}
                  </span>
                </li>
              ))}
            </ul>

            <h2>How we measure this</h2>
            <p>
              We hit <code>/api/health</code> from this page so you see the same number we do. The full uptime history,
              incident archive, and 90-day SLA report live on the external status page powered by Better Stack.
            </p>

            <h2>Subscribe to incidents</h2>
            <p>
              Get push notifications, email alerts, or RSS by clicking <strong>Get updates</strong> on the{' '}
              <a href={externalStatus} target="_blank" rel="noreferrer">
                external status page
              </a>
              .
            </p>
          </div>
        </article>
      </div>

      <footer className="legal-footer">
        <div className="container legal-footer-inner">
          <span>&copy; 2026 Rynora.</span>
          <span className="footer-bottom-links">
            <Link to="/docs">Docs</Link>
            <Link to="/contact">Contact</Link>
            <a href={externalStatus} target="_blank" rel="noreferrer">
              Status
            </a>
          </span>
        </div>
      </footer>

      <style>{`
        .status-headline {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
          margin: 24px 0 32px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--surface);
        }
        .status-headline strong {
          font-size: 16px;
          color: var(--text);
        }
        .status-headline-link {
          margin-left: auto;
          font-size: 13px;
          color: var(--accent);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .status-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #94a3b8;
          flex-shrink: 0;
        }
        .status-dot.is-up {
          background: #22c55e;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.18);
        }
        .status-dot.is-down {
          background: #ef4444;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.18);
        }
        .status-dot.is-pending {
          background: #94a3b8;
        }
        .status-services {
          list-style: none !important;
          padding: 0 !important;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .status-service {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--surface);
        }
        .status-service-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: var(--radius-md);
          background: var(--accent-glow);
          color: var(--accent);
          flex-shrink: 0;
        }
        .status-service strong {
          font-size: 14px;
          color: var(--text);
        }
        .status-service p {
          font-size: 12px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }
        .status-service > div { flex: 1; min-width: 0; }
        .status-pill {
          font-size: 12px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(148, 163, 184, 0.18);
          color: var(--text-muted);
        }
        .status-pill.is-up {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        [data-theme='dark'] .status-pill.is-up {
          color: #4ade80;
        }
        .status-pill.is-down {
          background: rgba(239, 68, 68, 0.15);
          color: #b91c1c;
        }
        [data-theme='dark'] .status-pill.is-down {
          color: #f87171;
        }
      `}</style>
    </div>
  );
}
